import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useEquipmentPickups } from '@/hooks/useEquipmentPickups';

interface EquipmentPickup {
  id: string;
  project_name: string;
  employee_name: string;
  description: string | null;
  photo_count: number;
  created_at: string;
}

interface EquipmentDetailModalProps {
  open: boolean;
  onClose: () => void;
  pickup: EquipmentPickup;
  onResolved: () => void;
}

export const EquipmentDetailModal: React.FC<EquipmentDetailModalProps> = ({
  open,
  onClose,
  pickup,
  onResolved,
}) => {
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const { resolvePickup, getPhotoUrls } = useEquipmentPickups();

  useEffect(() => {
    if (open && pickup.photo_count > 0) {
      setLoadingPhotos(true);
      getPhotoUrls(pickup.id)
        .then(setPhotoUrls)
        .finally(() => setLoadingPhotos(false));
    }
  }, [open, pickup.id, pickup.photo_count]);

  const handleResolve = async () => {
    setResolving(true);
    const success = await resolvePickup(pickup.id);
    setResolving(false);
    if (success) onResolved();
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fr-CA', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{pickup.project_name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Signalé par {pickup.employee_name} — {formatDate(pickup.created_at)}
            </div>

            {pickup.description && (
              <div>
                <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                  Description
                </div>
                <p className="text-sm">{pickup.description}</p>
              </div>
            )}

            {pickup.photo_count > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">
                  Photos ({pickup.photo_count})
                </div>
                {loadingPhotos ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {photoUrls.map((url, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedPhoto(url)}
                        className="aspect-square rounded-lg overflow-hidden bg-muted"
                      >
                        <img
                          src={url}
                          alt={`Équipement ${i + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={handleResolve}
                disabled={resolving}
              >
                {resolving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> En cours...</>
                ) : (
                  '✓ Récupéré'
                )}
              </Button>
              <Button variant="outline" className="flex-1" onClick={onClose}>
                Fermer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {selectedPhoto && (
        <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
          <DialogContent className="sm:max-w-2xl p-0">
            <img
              src={selectedPhoto}
              alt="Photo équipement"
              className="w-full h-auto rounded-lg"
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
